import { memo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button } from '@mui/material';
import { closeDialog } from 'app/store/fuse/dialogSlice';
import ReactCrop, { type Crop, PixelCrop } from 'react-image-crop';

interface CropImageModalModalProps {
  imgSrc?: string;
  onUploadCroppedImage: (blob: Blob) => void;
  circularCrop?: boolean;
}

const CropImageModal = ({
  imgSrc,
  onUploadCroppedImage,
  circularCrop = false,
}: CropImageModalModalProps) => {
  const dispatch = useDispatch();

  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const hiddenAnchorRef = useRef<HTMLAnchorElement>(null);
  const blobUrlRef = useRef<string>('');

  const closeModal = () => {
    dispatch(closeDialog());
  };

  const handleFileMutation = () => {
    const image = imgRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!image || !previewCanvas || !completedCrop) {
      throw new Error('Crop canvas does not exist');
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const ctx = previewCanvas.getContext('2d');
    if (!ctx) {
      throw new Error('No 2d context');
    }

    previewCanvas.width = completedCrop.width;
    previewCanvas.height = completedCrop.height;

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height,
    );
    return previewCanvas;
  };

  const onDownloadCropClick = async () => {
    handleFileMutation().toBlob((blob) => {
      if (!blob) {
        throw new Error('Failed to create blob');
      }

      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
      blobUrlRef.current = URL.createObjectURL(blob);

      if (hiddenAnchorRef.current) {
        hiddenAnchorRef.current.href = blobUrlRef.current;
        hiddenAnchorRef.current.click();
      }
      closeModal();
    }, 'image/png');
  };

  const onReplaceImageClick = async () => {
    handleFileMutation().toBlob((blob) => {
      if (!blob) {
        throw new Error('Failed to create blob');
      }

      onUploadCroppedImage(blob);
      closeModal();
    }, 'image/png');
  };

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        className='modal-content'
        style={{
          backgroundColor: 'white',
          padding: '20px',
          overflowY: 'auto',
          boxSizing: 'border-box',
        }}
      >
        <ReactCrop
          crop={crop}
          onChange={(c) => setCrop(c)}
          onComplete={(c) => setCompletedCrop(c)}
          aspect={circularCrop ? 1 : 16 / 9}
          circularCrop={circularCrop}
        >
          <img ref={imgRef} src={imgSrc} alt='company logo' />
        </ReactCrop>
      </Box>
      <a
        href='#hidden'
        ref={hiddenAnchorRef}
        download
        style={{
          position: 'absolute',
          top: '-200vh',
          visibility: 'hidden',
        }}
      >
        Hidden download
      </a>
      <canvas ref={previewCanvasRef} style={{ display: 'none' }} />
      <Box
        className='pt-20 pr-20 gap-8 w-full'
        display='flex'
        justifyContent='flex-end'
        sx={{ borderTop: '1px solid gray' }}
      >
        <Button onClick={closeModal} color='secondary' variant='contained'>
          Close
        </Button>
        <Button onClick={onReplaceImageClick} color='primary' variant='contained'>
          Replace Images
        </Button>
        <Button
          onClick={onDownloadCropClick}
          type='submit'
          color='primary'
          autoFocus
          variant='contained'
        >
          Download Image
        </Button>
      </Box>
    </Box>
  );
};

export default memo(CropImageModal);
